/**
 * ACI (AutoCAD Color Index) utilities — the full 1-255 index space mapped to
 * the classic palette. Entities store colors as hex strings (data.color),
 * so the picker renders ACI swatches and commits their resolved hex; the
 * index semantics (ByLayer = 256, ByBlock = 0) ride as sentinel strings.
 *
 * Algorithm (standard ACI grid): indices 10-249 are 24 hues × 10 tints —
 * rows 0-4 are full saturation descending lightness, rows 5-9 are the
 * half-saturation (pastel) versions of the same five steps; 250-255 are the
 * grayscale ramp; 1-9 are the fixed standard colors.
 */

/** Standard ACI 1-9 (7 renders white on dark canvases, black on light). */
export const ACI_STANDARD: Record<number, string> = {
    1: '#ff0000',
    2: '#ffff00',
    3: '#00ff00',
    4: '#00ffff',
    5: '#0000ff',
    6: '#ff00ff',
    7: '#ffffff',
    8: '#808080',
    9: '#c0c0c0',
};

/** ACI 250-255 grayscale ramp (dark → light; 255 = white). */
export const ACI_GRAYSCALE = [
    '#333333', '#505050', '#696969', '#828282', '#bebebe', '#ffffff',
];

/** Sentinel for the ByLayer color resolution (entity follows its layer). */
export const COLOR_BYLAYER = 'BYLAYER';
/** Sentinel for the ByBlock color resolution (entity follows its INSERT). */
export const COLOR_BYBLOCK = 'BYBLOCK';

function hslToHex(h: number, s: number, l: number): string {
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const hp = h / 60;
    const x = c * (1 - Math.abs((hp % 2) - 1));
    let r = 0, g = 0, b = 0;
    if (hp < 1) { r = c; g = x; }
    else if (hp < 2) { r = x; g = c; }
    else if (hp < 3) { g = c; b = x; }
    else if (hp < 4) { g = x; b = c; }
    else if (hp < 5) { r = x; b = c; }
    else { r = c; b = x; }
    const m = l - c / 2;
    const to = (v: number) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
    return `#${to(r)}${to(g)}${to(b)}`;
}

/** Resolve an ACI index (1-255) to its classic palette hex. */
export function aciToHex(aci: number): string {
    if (aci <= 0) return COLOR_BYBLOCK;
    if (aci <= 9) return ACI_STANDARD[aci] ?? '#ffffff';
    if (aci >= 250) return ACI_GRAYSCALE[Math.min(255, aci) - 250];
    const base = aci - 10;
    const hue = (Math.floor(base / 10) * 15) % 360;
    const step = base % 10;
    const full = step < 5;
    const lightness = full ? 0.5 - step * 0.05 : 0.75 - (step - 5) * 0.05;
    const saturation = full ? 1 : 0.5;
    return hslToHex(hue, saturation, lightness);
}

/** Nearest ACI index for a hex color (exact standard hits win first). */
export function hexToAci(hex: string): number | null {
    const norm = hex.toLowerCase();
    for (const [aci, h] of Object.entries(ACI_STANDARD)) {
        if (h === norm) return Number(aci);
    }
    if (ACI_GRAYSCALE.includes(norm)) return 250 + ACI_GRAYSCALE.indexOf(norm);
    for (let aci = 10; aci <= 249; aci++) {
        if (aciToHex(aci) === norm) return aci;
    }
    return null;
}

/** Classic ACI name for tooltips ("Red", "Yellow", ..., "Color 137"). */
export function aciName(aci: number): string {
    const names: Record<number, string> = {
        1: 'Red', 2: 'Yellow', 3: 'Green', 4: 'Cyan', 5: 'Blue',
        6: 'Magenta', 7: 'White', 8: 'Gray', 9: 'Light gray',
    };
    return names[aci] ?? `Color ${aci}`;
}

/** The full selectable index list for the picker grid (standard + grid + grays). */
export const ACI_GRID: number[] = [
    ...Object.keys(ACI_STANDARD).map(Number).sort((a, b) => a - b),
    ...Array.from({ length: 240 }, (_, i) => 10 + i),
    ...Array.from({ length: 6 }, (_, i) => 250 + i),
];
